import { Button, CircularProgress, MenuItem, TextField } from '@mui/material';
import { Plus } from 'lucide-react';
import React from 'react';
import type { Agent, IntegrationClient } from '../../types';
import { useTranslation } from 'react-i18next';
import { API_KEY_USE_CASES, INTEGRATION_CLIENT_ENVIRONMENTS } from './constants';

export interface NewClientState {
  name: string;
  environment: string;
}

interface CreateApiKeyResourceSelectProps {
  config: (typeof API_KEY_USE_CASES)[number];
  needsIntegrationClient: boolean;
  needsAgent: boolean;
  integrationClients: IntegrationClient[];
  agents: Agent[];
  clientPublicId: string;
  onClientChange: (publicId: string) => void;
  agentPublicId: string;
  onAgentChange: (publicId: string) => void;
  newClient: NewClientState;
  onNewClientChange: (client: NewClientState) => void;
  creatingClient: boolean;
  onCreateClient: () => void;
}

export const CreateApiKeyResourceSelect: React.FC<CreateApiKeyResourceSelectProps> = ({
  config,
  needsIntegrationClient,
  needsAgent,
  integrationClients,
  agents,
  clientPublicId,
  onClientChange,
  agentPublicId,
  onAgentChange,
  newClient,
  onNewClientChange,
  creatingClient,
  onCreateClient,
}) => {
  const { t } = useTranslation();

  return (
    <div>
      <p className="mb-2 text-sm font-semibold text-gray-800 dark:text-slate-200">
        {t(config.resourceLabelKey, {
          defaultValue: config.resourceLabel,
        })}{' '}
        <span className="text-red-500">*</span>
      </p>

      {needsIntegrationClient ? (
        <div className="space-y-3">
          <TextField
            select
            fullWidth
            label={t('apiKeys.modal.integrationClientResource')}
            value={clientPublicId}
            onChange={(e) => onClientChange(e.target.value)}
            size="small"
            helperText={
              integrationClients.length === 0
                ? t('apiKeys.modal.noIntegrationClientsHelper')
                : t('apiKeys.modal.integrationClientHelper')
            }
          >
            <MenuItem value="">{t('apiKeys.modal.none')}</MenuItem>
            {integrationClients.map((client) => (
              <MenuItem key={client.public_id} value={client.public_id}>
                {client.name} · {client.environment} · {client.public_id}
              </MenuItem>
            ))}
          </TextField>

          <div className="rounded-lg border border-gray-200 p-3 dark:border-slate-700">
            <p className="mb-2 text-xs font-semibold uppercase text-gray-500 dark:text-slate-400">
              {t('apiKeys.modal.createIntegrationClient')}
            </p>
            <div className="grid gap-2 md:grid-cols-[1fr_160px_auto]">
              <TextField
                fullWidth
                label={t('apiKeys.modal.clientName')}
                value={newClient.name}
                onChange={(e) => onNewClientChange({ ...newClient, name: e.target.value })}
                size="small"
                placeholder="NailMap Production"
              />
              <TextField
                select
                fullWidth
                label={t('apiKeys.modal.environment')}
                value={newClient.environment}
                onChange={(e) => onNewClientChange({ ...newClient, environment: e.target.value })}
                size="small"
              >
                {INTEGRATION_CLIENT_ENVIRONMENTS.map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                type="button"
                variant="outlined"
                disabled={!newClient.name.trim() || creatingClient}
                onClick={onCreateClient}
                startIcon={creatingClient ? <CircularProgress size={16} /> : <Plus size={16} />}
              >
                Create
              </Button>
            </div>
          </div>
        </div>
      ) : needsAgent ? (
        <TextField
          select
          fullWidth
          label={t('apiKeys.modal.agentResource')}
          value={agentPublicId}
          onChange={(e) => onAgentChange(e.target.value)}
          size="small"
          helperText={
            agents.length === 0 ? t('apiKeys.modal.noAgentsHelper') : t('apiKeys.modal.agentHelper')
          }
        >
          <MenuItem value="">{t('apiKeys.modal.none')}</MenuItem>
          {agents.map((agent) => (
            <MenuItem key={agent.publicId} value={agent.publicId}>
              {agent.name} · {agent.status || 'draft'} · {agent.publicId}
            </MenuItem>
          ))}
        </TextField>
      ) : null}
    </div>
  );
};
